import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { of } from 'rxjs';
import { EventRefreshService } from 'src/app/services/event-refresh.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError } from 'rxjs/operators';
import { EventService } from 'src/app/services/event.service';
import { LocationService } from 'src/app/services/location.service';
import {
  AppEvent,
  Recommandation,
  EVENT_TYPES,
} from 'src/app/models/event.model';
import { EventWeatherForecast } from 'src/app/models/location.models';

export interface EventWithReco {
  event: AppEvent;
  recommandations: Recommandation[];
  expanded: boolean;
  loading: boolean;
  recoError: boolean;
  weather: EventWeatherForecast | null; // null = not yet fetched
}

@Component({
  selector: 'app-recommendations-itinerant',
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './recommendations-itinerant.component.html',
  styleUrls: ['./recommendations-itinerant.component.css'],
})
export class RecommendationsItinerantComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  eventRows: EventWithReco[] = [];
  isLoading = true;
  hasError = false;

  sortBy: 'date' | 'name' = 'date';

  constructor(
    private router: Router,
    private eventService: EventService,
    private locationService: LocationService,
    private eventRefreshService: EventRefreshService,
  ) {}

  ngOnInit() {
    this.loadEvents();
    this.eventRefreshService.dataChange$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadEvents());
    this.eventRefreshService.transitions$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.pruneEndedEvents();
        this.cdr.markForCheck();
      });
  }

  loadEvents() {
    this.isLoading = true;
    this.hasError = false;

    this.eventService
      .getEvents({})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (events) => {
          const upcoming = events
            .filter((e) => this.isActiveOrUpcoming(e))
            .sort(
              (a, b) =>
                new Date(a.eventDate).getTime() -
                new Date(b.eventDate).getTime(),
            );

          this.eventRows = upcoming.map((e) => ({
            event: e,
            recommandations: [],
            expanded: false,
            loading: false,
            recoError: false,
            weather: null,
          }));
          this.isLoading = false;
          this.eventRefreshService.scheduleTransitions(upcoming);
          this.fetchWeatherForAll();
          this.cdr.markForCheck();
        },
        error: () => {
          this.hasError = true;
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  toggleDetails(row: EventWithReco) {
    row.expanded = !row.expanded;
    if (row.expanded && row.recommandations.length === 0 && !row.recoError) {
      this.loadRecos(row);
    }
  }

  loadRecos(row: EventWithReco) {
    row.loading = true;
    row.recoError = false;
    this.eventService
      .getRecommandations(row.event.id)
      .pipe(catchError(() => of([])))
      .subscribe((recos) => {
        row.recommandations = recos;
        row.loading = false;
        if (recos.length === 0) row.recoError = true;
        this.cdr.markForCheck();
      });
  }

  /** Charge la météo pour chaque événement (coords + date) en arrière-plan */
  private fetchWeatherForAll(): void {
    for (const row of this.eventRows) {
      const loc = row.event.location as any;
      const lat = loc?.coordinates?.lat ?? loc?.lat ?? null;
      const lng = loc?.coordinates?.lng ?? loc?.lng ?? null;
      if (!lat || !lng || !row.event.eventDate) continue;

      const dateStr = new Date(row.event.eventDate).toISOString().slice(0, 10);
      this.locationService
        .getWeatherForDate(lat, lng, dateStr)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((w) => {
          row.weather = w;
          this.cdr.markForCheck();
        });
    }
  }

  private isActiveOrUpcoming(e: AppEvent): boolean {
    const now = new Date();
    const eventDay = new Date(e.eventDate);
    eventDay.setHours(0, 0, 0, 0);
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    if (eventDay < today) return false;
    if (eventDay > today) return true;
    // same day — check endTime
    const [h, m] = (e.endTime || '23:59').split(':').map(Number);
    const endDt = new Date(e.eventDate);
    endDt.setHours(h, m, 0, 0);
    return endDt > now;
  }

  private pruneEndedEvents(): void {
    this.eventRows = this.eventRows.filter((r) =>
      this.isActiveOrUpcoming(r.event),
    );
  }

  resolveDisplayStatus(e: AppEvent): 'planned' | 'active' | 'completed' {
    const now = new Date();
    const eventDay = new Date(e.eventDate);
    eventDay.setHours(0, 0, 0, 0);
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    if (eventDay > today) return 'planned';
    if (eventDay < today) return 'completed';
    const [sh, sm] = (e.startTime || '00:00').split(':').map(Number);
    const startDt = new Date(e.eventDate);
    startDt.setHours(sh, sm, 0, 0);
    const [eh, em] = (e.endTime || '23:59').split(':').map(Number);
    const endDt = new Date(e.eventDate);
    endDt.setHours(eh, em, 0, 0);
    if (endDt <= now) return 'completed';
    if (startDt <= now) return 'active';
    return 'planned';
  }

  weatherIcon(row: EventWithReco): string {
    if (!row.weather) return '⏳';
    if (!row.weather.available) return '⏳';
    const code = (row.weather as any).weatherCode;
    if (code === 0 || code === 1) return '☀️';
    if (code === 2) return '🌤️';
    if (code === 3) return '☁️';
    if (code === 45 || code === 48) return '🌫️';
    if (code >= 51 && code <= 55) return '🌧️';
    if (code >= 61 && code <= 65) return '🌧️';
    if (code >= 71 && code <= 77) return '❄️';
    if (code >= 80 && code <= 82) return '🌦️';
    if (code >= 85 && code <= 86) return '🌨️';
    if (code >= 95) return '⚡';
    return '🌡️';
  }

  weatherLabel(row: EventWithReco): string {
    if (!row.weather) return 'Météo en cours...';
    if (!row.weather.available) return 'Météo non disponible (> 15j)';
    const w = row.weather as Extract<EventWeatherForecast, { available: true }>;
    const min = Math.round(w.temperatureMin);
    const max = Math.round(w.temperatureMax);
    return `${w.condition} • ${min}–${max}°C`;
  }

  weatherBadgeClass(row: EventWithReco): string {
    if (!row.weather || !row.weather.available)
      return 'bg-gray-100 text-gray-500';
    const code = (row.weather as any).weatherCode;
    if (code >= 95) return 'bg-red-100 text-red-700';
    if ((code >= 61 && code <= 82) || (code >= 51 && code <= 55))
      return 'bg-blue-100 text-blue-700';
    if (code === 0 || code === 1) return 'bg-yellow-50 text-yellow-700';
    return 'bg-gray-100 text-gray-600';
  }

  get sortedRows(): EventWithReco[] {
    const rows = [...this.eventRows];
    if (this.sortBy === 'date') {
      rows.sort(
        (a, b) =>
          new Date(a.event.eventDate).getTime() -
          new Date(b.event.eventDate).getTime(),
      );
    } else {
      rows.sort((a, b) => a.event.name.localeCompare(b.event.name));
    }
    return rows;
  }

  get totalItems(): number {
    return this.eventRows.reduce(
      (sum, r) =>
        sum + r.recommandations.reduce((s, rec) => s + rec.recommended, 0),
      0,
    );
  }

  get totalEstimatedRevenue(): string {
    const total = this.eventRows.reduce((sum, r) => {
      const rev = r.event.estimatedRevenue;
      if (!rev) return sum;
      const mid =
        typeof rev === 'number' ? rev : ((rev.low ?? 0) + (rev.high ?? 0)) / 2;
      return sum + mid;
    }, 0);
    if (total === 0) return '—';
    return total.toLocaleString('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    });
  }

  getEventTypeIcon(type: string): string {
    return EVENT_TYPES.find((t) => t.value === type)?.icon ?? '📅';
  }

  getEventTypeLabel(type: string): string {
    return EVENT_TYPES.find((t) => t.value === type)?.label ?? type;
  }

  getLocationLabel(loc: any): string {
    if (!loc) return '';
    if (typeof loc === 'string') return loc;
    return [loc.address, loc.city].filter(Boolean).join(', ');
  }

  formatDate(d: any): string {
    return new Date(d).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  getConfidenceColor(c: number): string {
    if (c >= 80) return 'text-green-600';
    if (c >= 60) return 'text-yellow-600';
    return 'text-red-500';
  }

  navigateToCreateEvent() {
    this.router.navigate(['/events/create']);
  }

  navigateToEvent(id: string) {
    this.router.navigate(['/events', id]);
  }
}
